import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import { ActivityTracker, activityTracker } from './activityTracker'
import { ACTIVITY_HEADERS, buildActivityHeaders } from './headers'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * false = this request is never tagged with the user action in progress (activity
     * history), e.g. a request the page makes on its own while the user works.
     */
    trackActivity?: boolean
    /** Internal (activity tracking): the key of the request in the tracker. */
    activityRequestKey?: number
  }
}

/**
 * Requests that never carry the activity headers: the token endpoint, in-app notifications, the
 * assistant, the history itself, heartbeats. Matched against the full URL (baseURL + url).
 */
export const ACTIVITY_EXCLUDED_URLS: ReadonlyArray<RegExp> = [
  /(?:^|\/)connect\/token(?:[/?#]|$)/i,
  /(?:^|\/)app\/notifications\/inapp(?:[/?#]|$)/i,
  /(?:^|\/)app\/assistant(?:[-/?#]|$)/i,
  /(?:^|\/)app\/activity-history(?:[/?#]|$)/i,
  /heartbeat/i,
]

const ABSOLUTE_URL = /^[a-z][a-z\d+.-]*:\/\//i

/** The URL a request goes to: its url, prefixed with its baseURL unless it is absolute. */
export const requestUrlOf = (
  config: Pick<InternalAxiosRequestConfig, 'url' | 'baseURL'> | undefined
): string => {
  const url = config?.url ?? ''
  const base = config?.baseURL ?? ''
  if (!base || ABSOLUTE_URL.test(url)) {
    return url
  }
  return `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
}

const readHeader = (headers: any, name: string): string | undefined => {
  if (!headers) {
    return undefined
  }
  const value =
    typeof headers.get === 'function' ? headers.get(name) : headers[name]
  return value === null || value === undefined || value === ''
    ? undefined
    : String(value)
}

const writeHeaders = (
  config: InternalAxiosRequestConfig,
  values: Record<string, string>
) => {
  if (!config.headers) {
    config.headers = {} as InternalAxiosRequestConfig['headers']
  }
  const headers: any = config.headers
  Object.entries(values).forEach(([name, value]) => {
    if (typeof headers.set === 'function') {
      headers.set(name, value)
    } else {
      headers[name] = value
    }
  })
}

/** The config of the request a settled response or error belongs to. */
const configOf = (settled: any): InternalAxiosRequestConfig | undefined =>
  settled?.config ?? settled?.response?.config

export interface ActivityInterceptors {
  onRequest: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
  onResponse: (response: AxiosResponse) => AxiosResponse
  onResponseError: (error: any) => Promise<never>
}

/**
 * The axios interceptors of activity tracking. The request interceptor adds the X-Activity-*
 * headers of the open scope (unless the user has since acted in another subject's tab, see
 * ActivityTracker.noteInteraction) to every request (GETs included) except silent ones, ones with
 * `trackActivity: false` and excluded URLs, and counts the request as in flight in the scope; the
 * response interceptors count it as settled. A request that already carries an activity id (the
 * retry after a token refresh re-sends the same config) keeps its headers. Tracking never makes
 * a request fail: any error of its own is swallowed.
 */
export const createActivityInterceptors = (
  tracker: ActivityTracker = activityTracker,
  excludedUrls: ReadonlyArray<RegExp> = ACTIVITY_EXCLUDED_URLS
): ActivityInterceptors => {
  const isExcluded = (config: InternalAxiosRequestConfig) => {
    const url = requestUrlOf(config)
    return excludedUrls.some(pattern => pattern.test(url))
  }

  const finish = (config: InternalAxiosRequestConfig | undefined) => {
    try {
      tracker.finishRequest(config?.activityRequestKey)
    } catch {
      // Tracking must never break a request.
    }
  }

  return {
    onRequest: config => {
      try {
        if (
          !config ||
          (config as { silent?: boolean }).silent === true ||
          config.trackActivity === false ||
          isExcluded(config)
        ) {
          return config
        }
        const taggedWith = readHeader(config.headers, ACTIVITY_HEADERS.id)
        if (taggedWith) {
          if (!tracker.isRequestOpen(config.activityRequestKey)) {
            config.activityRequestKey =
              tracker.rejoinRequest(taggedWith) ?? undefined
          }
          return config
        }
        const tracked = tracker.trackRequest()
        if (!tracked) {
          return config
        }
        writeHeaders(config, buildActivityHeaders(tracked.scope))
        config.activityRequestKey = tracked.key
      } catch {
        // Tracking must never break a request.
      }
      return config
    },
    onResponse: response => {
      finish(configOf(response))
      return response
    },
    onResponseError: error => {
      finish(configOf(error))
      return Promise.reject(error)
    },
  }
}

const appInterceptors = createActivityInterceptors()

/**
 * Tags the requests of an axios instance with the user action in progress. Attach it before the
 * instance's other response interceptors: they may turn the error into something without a
 * config (a message string), and the in-flight count could not be settled then.
 */
export const attachActivityTracking = (
  instance: AxiosInstance,
  interceptors: ActivityInterceptors = appInterceptors
) => {
  instance.interceptors.request.use(interceptors.onRequest, undefined, {
    synchronous: true,
  })
  instance.interceptors.response.use(
    interceptors.onResponse,
    interceptors.onResponseError
  )
}
